import { apiRequest } from './apiClient';
import AuthServiceV2 from './authServiceV2';
import { getApiUrl } from '../config/api';
import { AUTH_ROLES } from '../utils/authConstants';

class AppSettingsService {
  static unwrapData(resultData) {
    if (resultData && typeof resultData === 'object' && 'data' in resultData) return resultData.data;
    return resultData;
  }

  static getMediaUrl(url) {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return getApiUrl(url);
  }

  static async getLoginBanner() {
    return apiRequest({
      endpoint: '/admin/api/public/settings/login-banner',
      method: 'GET',
      auth: false,
      suppressErrorToast: true,
    });
  }

  static async uploadLoginBanner(file) {
    const formData = new FormData();
    formData.append('image', file);

    const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN) || localStorage.getItem('access_token');

    const response = await fetch(getApiUrl('/admin/api/settings/login-banner'), {
      method: 'POST',
      headers: {
        accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const text = await response.text();
    let result = null;
    if (text) {
      try {
        result = JSON.parse(text);
      } catch {
        result = { message: text };
      }
    }

    const backendStatus = result?.status;
    const backendCode = result?.status_code;
    const isBackendSuccess = backendStatus === true && (backendCode === 200 || backendCode === 201);

    if (response.ok && (isBackendSuccess || !('status' in (result || {})))) {
      return {
        success: true,
        data: this.unwrapData(result?.data ?? result),
        message: result?.message || 'Banner updated',
        raw: result,
      };
    }

    return {
      success: false,
      message: result?.message || `Upload failed (${response.status})`,
      raw: result,
    };
  }

  static async clearLoginBanner() {
    return apiRequest({
      endpoint: '/admin/api/settings/login-banner',
      method: 'DELETE',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      showSuccess: false,
    });
  }

  static async getQuizInstructions() {
    return apiRequest({
      endpoint: '/admin/api/public/settings/quiz-instructions',
      method: 'GET',
      auth: false,
      suppressErrorToast: true,
    });
  }

  static async updateQuizInstructions(payload) {
    return apiRequest({
      endpoint: '/admin/api/settings/quiz-instructions',
      method: 'POST',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      body: payload,
      showSuccess: false,
    });
  }

  static async resetQuizInstructions() {
    return apiRequest({
      endpoint: '/admin/api/settings/quiz-instructions',
      method: 'DELETE',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      showSuccess: false,
    });
  }

  static async getSecuritySettings() {
    return apiRequest({
      endpoint: '/admin/api/public/settings/security',
      method: 'GET',
      auth: false,
      suppressErrorToast: true,
    });
  }

  static async updateSecuritySettings(payload) {
    return apiRequest({
      endpoint: '/admin/api/settings/security',
      method: 'POST',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      body: payload,
      showSuccess: false,
    });
  }

  static async resetSecuritySettings() {
    return apiRequest({
      endpoint: '/admin/api/settings/security',
      method: 'DELETE',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      showSuccess: false,
    });
  }

  static async getSessionPolicy() {
    return apiRequest({
      endpoint: '/admin/api/public/settings/session-policy',
      method: 'GET',
      auth: false,
      suppressErrorToast: true,
    });
  }

  static async updateSessionPolicy(payload) {
    return apiRequest({
      endpoint: '/admin/api/settings/session-policy',
      method: 'POST',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      body: payload,
      showSuccess: false,
    });
  }

  static async resetSessionPolicy() {
    return apiRequest({
      endpoint: '/admin/api/settings/session-policy',
      method: 'DELETE',
      auth: true,
      role: AUTH_ROLES.ADMIN,
      showSuccess: false,
    });
  }
}

export default AppSettingsService;
